/**
 * The authentication context object.
 *
 * Lives in `lib/`, not in `features/auth/`, because the session is
 * cross-cutting: the app shell needs it to filter navigation and to greet the
 * user, and a shared component must not import a feature (plan §3.1). The
 * PROVIDER stays in the feature, since only the feature owns how a session is
 * obtained.
 *
 * Split from the provider so the context and its hook can be imported without
 * dragging the provider's dependencies along — and so each file exports one
 * kind of thing, which is what fast refresh needs.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.13
 */
import { createContext, useContext } from 'react';

import type { LoginRequest, Role, SessionUser } from '@nexus/contracts';

export interface AuthContextValue {
  user: SessionUser | null;
  /** True until the initial session restore has settled. */
  isLoading: boolean;
  signIn: (credentials: LoginRequest) => Promise<void>;
  signOut: () => Promise<void>;
  /** True when the signed-in user meets the required role (PRD §4). */
  can: (required: Role) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Reads the authentication context.
 *
 * @throws when used outside the provider — a clear message at the point of the
 *   mistake, rather than `undefined.user` somewhere further down.
 */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (value === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return value;
}
